<template>
  <v-card class="pa-6 mt-4 mx-2" color="white" elevation="2" rounded="xl">
    <!-- Filtros principales -->
    <v-row justify="center" class="align-center mb-2">
      <v-col cols="12" md="2" class="py-2">
        <v-autocomplete v-model="business_selected" :items="$store.state.auth.bussines_units" label="Unidad de negocio"
          color="primary" item-color="primary" outlined dense hide-details required @change="refresFilters()">
          <template v-slot:prepend-inner>
            <v-icon color="primary">mdi-office-building-outline</v-icon>
          </template>
        </v-autocomplete>
      </v-col>
      <v-col cols="12" md="2" class="py-2">
        <v-autocomplete v-model="location_selected" :items="locationsFrombusiness" label="Sucursal" color="primary"
          item-color="primary" outlined dense hide-details return-object required @change="refresFilters()">
          <template v-slot:prepend-inner>
            <v-icon color="primary">mdi-map-marker-outline</v-icon>
          </template>
        </v-autocomplete>
      </v-col>

      <v-col cols="12" md="2" class="py-2">
        <v-menu v-model="warehouseMenuOpen" :close-on-content-click="false" :max-width="600" offset-y>
          <template v-slot:activator="{ on, attrs }">
            <v-text-field v-model="warehouseDisplayText" label="Almacén" color="primary" outlined dense hide-details
              readonly v-bind="attrs" v-on="on" @click="loadWarehouseTree">
              <template v-slot:prepend-inner>
                <v-icon color="primary">mdi-warehouse</v-icon>
              </template>
              <template v-slot:append>
                <v-icon color="primary">mdi-chevron-down</v-icon>
              </template>
            </v-text-field>
          </template>

          <v-card class="warehouse-select-card" elevation="8">
            <v-card-title class="warehouse-select-title pa-4">
              <div class="d-flex align-center flex-grow-1">
                <v-avatar color="primary" size="40" class="mr-3">
                  <v-icon color="white">mdi-warehouse</v-icon>
                </v-avatar>
                <div>
                  <h4 class="warehouse-title">Seleccionar Almacén</h4>
                  <p class="warehouse-subtitle mb-0">
                    {{ warehouseTree.length }} almacén{{
                      warehouseTree.length !== 1 ? "es" : ""
                    }}
                    disponible{{ warehouseTree.length !== 1 ? "s" : "" }}
                    <span v-if="searchTerm && searchMatchCount" class="search-results ml-2">
                      ({{ searchMatchCount }} encontrado{{
                        searchMatchCount !== 1 ? "s" : ""
                      }})
                    </span>
                  </p>
                </div>
              </div>

              <div class="warehouse-controls">
                <!-- Search mejorado -->
                <v-text-field v-model="searchTerm" prepend-inner-icon="mdi-magnify" label="Buscar almacén..." outlined
                  dense hide-details clearable class="search-field mr-2" @input="onSearchInput"
                  @click:clear="clearSearch">
                  <template v-slot:append-outer v-if="searchTerm && searchMatchCount">
                    <v-chip x-small color="success" class="ml-1">
                      {{ searchMatchCount }}
                    </v-chip>
                  </template>
                </v-text-field>

                <!-- Expand/Collapse buttons -->
                <div class="d-flex">
                  <v-btn icon small color="primary" class="mr-1" title="Expandir todos los nodos"
                    @click="expandAllNodes" :loading="loading">
                    <v-icon small>mdi-arrow-expand-all</v-icon>
                  </v-btn>
                  <v-btn icon small color="grey darken-1" title="Contraer todos los nodos" @click="collapseAllNodes"
                    :loading="loading">
                    <v-icon small>mdi-arrow-collapse-all</v-icon>
                  </v-btn>
                </div>
              </div>
            </v-card-title>

            <v-divider></v-divider>

            <v-card-text class="pa-0" style="max-height: 400px; overflow-y: auto">
              <div v-if="loadingWarehouses" class="pa-4 text-center">
                <v-progress-circular indeterminate color="primary" size="24" class="mr-2"></v-progress-circular>
                Cargando almacenes...
              </div>
              <div v-else-if="currentTreeData.length === 0" class="empty-warehouse-state">
                <div class="empty-warehouse-content">
                  <v-icon size="60" color="grey lighten-1">
                    {{ searchTerm ? "mdi-magnify-scan" : "mdi-warehouse-off" }}
                  </v-icon>
                  <h4 class="empty-warehouse-title mt-3 mb-2">
                    {{
                      searchTerm
                        ? "No se encontraron almacenes"
                        : "No hay almacenes disponibles"
                    }}
                  </h4>
                  <p class="empty-warehouse-subtitle grey--text mb-3">
                    {{
                      searchTerm
                        ? "Intenta con otros términos de búsqueda"
                        : "Selecciona otra ubicación"
                    }}
                  </p>
                  <v-btn v-if="searchTerm" small color="grey darken-1" text @click="clearSearch">
                    <v-icon left small>mdi-close</v-icon>
                    Limpiar Búsqueda
                  </v-btn>
                </div>
              </div>
              <v-treeview v-else ref="warehouseTree" :items="currentTreeData" :search="internalSearchTerm"
                :filter="customFilter" item-key="id" item-text="name" item-children="children" dense hoverable
                activatable selectable single-select expand-icon="mdi-chevron-right" selected-color="primary"
                :active.sync="warehouseActive" :selected.sync="warehouseSelected" :open.sync="expandedNodes"
                return-object @update:active="handleActiveUpdate" @update:selected="handleSelectedUpdate"
                @update:open="handleOpenUpdate" class="warehouse-tree pa-2">
                <template v-slot:prepend="{ item }">
                  <v-icon :color="isNodeHighlighted(item)
                    ? 'primary'
                    : item.active
                      ? 'primary lighten-1'
                      : 'grey'
                    " :class="{ 'pulse-icon': searchTerm && isNodeHighlighted(item) }">
                    {{
                      item.children && item.children.length
                        ? "mdi-warehouse"
                        : "mdi-package-variant-closed"
                    }}
                  </v-icon>
                </template>

                <template v-slot:label="{ item }">
                  <div class="d-flex align-center warehouse-node">
                    <div>
                      <div class="warehouse-name" v-html="highlightSearchText(item.name)"></div>
                      <div class="warehouse-details d-flex align-items-center">
                        <v-chip x-small :color="item.active ? 'success' : 'grey'" text-color="white" class="mr-2"
                          v-if="item.active !== undefined">
                          {{ item.active ? "Activo" : "Inactivo" }}
                        </v-chip>
                        <v-chip v-if="item.children && item.children.length" x-small color="primary" text-color="white"
                          class="mr-2">
                          Grupo
                        </v-chip>
                        <span v-if="
                          item.location_description_short ||
                          item.location_description_long
                        " class="location-info caption grey--text" v-html="highlightSearchText(
                          item.location_description_short ||
                          item.location_description_long ||
                          ''
                        )
                          "></span>
                      </div>
                    </div>
                  </div>
                </template>
              </v-treeview>
            </v-card-text>

            <v-divider></v-divider>

            <v-card-actions class="pa-3">
              <v-btn text color="grey darken-1" @click="warehouseMenuOpen = false">
                <v-icon left small>mdi-close</v-icon>
                Cancelar
              </v-btn>
              <v-spacer></v-spacer>
              <v-btn v-if="show_all_option" color="primary" text @click="selectAllWarehouses">
                <v-icon left small>mdi-warehouse-outline</v-icon>
                Todos los almacenes
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-menu>
      </v-col>
      <v-col cols="12" sm="3" md="2" class="py-2">
        <v-autocomplete v-model="rangeSelected" :items="rangeTime" label="Rango de tiempo" color="primary"
          item-color="primary" outlined dense hide-details required @change="updateRange()">
          <template v-slot:prepend-inner>
            <v-icon color="primary">mdi-calendar-range</v-icon>
          </template>
        </v-autocomplete>
      </v-col>
      <v-col cols="12" sm="2" md="2" class="py-2" v-show="rangeSelected == '5' && show_date_start">
        <v-menu v-model="menuStart" :close-on-content-click="false" :nudge-right="40" transition="scale-transition"
          offset-y min-width="auto">
          <template v-slot:activator="{ on, attrs }">
            <v-text-field v-model="dateFormatDMY" label="Fecha desde" color="primary" outlined dense hide-details
              readonly v-bind="attrs" v-on="on">
              <template v-slot:prepend-inner>
                <v-icon color="primary">mdi-calendar</v-icon>
              </template>
            </v-text-field>
          </template>
          <v-date-picker v-model="dateStart" color="primary" header-color="primary" show-current locale="es"
            @input="refresFilters(), (menuStart = false)"></v-date-picker>
        </v-menu>
      </v-col>
      <v-col cols="12" sm="2" md="2" class="py-2" v-show="rangeSelected == '5'">
        <v-menu v-model="menuEnd" :close-on-content-click="false" :nudge-right="40" transition="scale-transition"
          offset-y min-width="auto">
          <template v-slot:activator="{ on, attrs }">
            <v-text-field v-model="date2FormatDMY" label="Fecha hasta" color="primary" outlined dense hide-details
              readonly v-bind="attrs" v-on="on">
              <template v-slot:prepend-inner>
                <v-icon color="primary">mdi-calendar</v-icon>
              </template>
            </v-text-field>
          </template>
          <v-date-picker :min="dateStart" v-model="dateEnd" color="primary" header-color="primary" show-current
            locale="es" @input="refresFilters(), (menuEnd = false)"></v-date-picker>
        </v-menu>
      </v-col>
      <v-col cols="12" md="2" class="py-2 d-flex justify-center">
        <v-btn :color="showAdvancedFilters ? 'primary' : 'grey'" :outlined="!showAdvancedFilters" rounded elevation="2"
          class="px-4" @click="showAdvancedFilters = !showAdvancedFilters">
          <v-icon left size="18">
            {{ showAdvancedFilters ? "mdi-filter-minus" : "mdi-filter-plus" }}
          </v-icon>
          {{ showAdvancedFilters ? "Ocultar" : "Filtros" }}
          <v-icon right size="16" class="ml-1">
            {{ showAdvancedFilters ? "mdi-chevron-up" : "mdi-chevron-down" }}
          </v-icon>
        </v-btn>
      </v-col>
    </v-row>

    <!-- Filtros avanzados expandibles -->
    <v-expand-transition>
      <v-card v-show="showAdvancedFilters" class="mt-4 pa-6" color="#f8f9fa" elevation="1" rounded="xl">
        <v-row justify="center" class="align-center">
          <v-col cols="12" class="pb-4">
            <div class="d-flex align-center justify-center">
              <v-avatar color="primary" size="32" class="mr-3">
                <v-icon color="white" size="18">mdi-filter-variant</v-icon>
              </v-avatar>
              <h3 class="primary--text font-weight-medium mb-0">Filtros Avanzados</h3>
            </div>
            <v-divider class="mt-3 mx-auto" style="max-width: 200px"></v-divider>
          </v-col>

          <!-- Primera fila de filtros -->

          <v-col cols="12" sm="6" md="4" lg="2" class="py-2">
            <v-autocomplete v-model="advancedFilters.unit" :items="units" item-value="id" item-text="name"
              color="primary" label="Unidad de medida" outlined dense clearable hide-details @change="refresFilters()">
              <template v-slot:prepend-inner>
                <v-icon color="primary" size="20">mdi-ruler</v-icon>
              </template>
            </v-autocomplete>
          </v-col>

          <v-col cols="12" sm="6" md="4" lg="2" class="py-2">
            <v-autocomplete v-model="advancedFilters.item_category" :items="categorys" item-value="id" item-text="name"
              color="primary" label="Categoría" outlined dense multiple clearable hide-details
              @change="refresFilters()">
              <template v-slot:prepend-inner>
                <v-icon color="primary" size="20">mdi-folder-outline</v-icon>
              </template>
            </v-autocomplete>
          </v-col>

          <v-col cols="12" sm="6" md="4" lg="2" class="py-2">
            <v-autocomplete v-model="advancedFilters.account_category" :items="accountCategorys" item-value="id"
              item-text="name" color="primary" label="Categoría de cuenta" outlined dense clearable hide-details
              @change="refresFilters()">
              <template v-slot:prepend-inner>
                <v-icon color="primary" size="20">mdi-account-group-outline</v-icon>
              </template>
            </v-autocomplete>
          </v-col>

          <v-col cols="12" sm="6" md="4" lg="2" class="py-2">
            <v-autocomplete v-model="advancedFilters.tax_type" :items="taxes" item-value="id" item-text="name"
              color="primary" label="Tipo de impuesto" outlined dense clearable hide-details @change="refresFilters()">
              <template v-slot:prepend-inner>
                <v-icon color="primary" size="20">mdi-percent</v-icon>
              </template>
            </v-autocomplete>
          </v-col>

          <v-col cols="12" sm="6" md="4" lg="2" class="py-2">
            <v-autocomplete v-model="advancedFilters.inventory_product" :items="[
              { value: 0, text: 'AMBOS' },
              { value: 1, text: 'SÍ' },
              { value: 2, text: 'NO' },
            ]" item-value="value" item-text="text" color="primary" label="Inventariable" outlined dense clearable
              hide-details @change="refresFilters()">
              <template v-slot:prepend-inner>
                <v-icon color="primary" size="20">mdi-package-variant</v-icon>
              </template>
            </v-autocomplete>
          </v-col>
          <v-col cols="12" sm="6" md="4" lg="2" class="py-2">
            <v-autocomplete v-model="advancedFilters.negative_sale" :items="[
              { value: 0, text: 'AMBOS' },
              { value: 1, text: 'SÍ' },
              { value: 2, text: 'NO' },
            ]" item-value="value" item-text="text" color="primary" label="Valores negativos" outlined dense clearable
              hide-details @change="refresFilters()">
              <template v-slot:prepend-inner>
                <v-icon color="primary" size="20">mdi-minus-circle-outline</v-icon>
              </template>
            </v-autocomplete>
          </v-col>
        </v-row>

        <!-- Segunda fila de filtros -->
        <v-row justify="center" class="mt-3">
          <v-col cols="12" sm="6" md="4" lg="2" class="py-2 d-flex align-center">
            <v-card class="pa-3 d-flex align-center" color="transparent" elevation="0" outlined rounded="lg">
              <v-icon color="primary" class="mr-2" size="20">mdi-eye-outline</v-icon>
              <v-checkbox v-model="advancedFilters.showAll"
                :label="advancedFilters.showAll ? 'Ver todo ' : 'Ver sin  existencia'" color="primary" dense
                hide-details class="mt-0 pt-0" @change="refresFilters()"></v-checkbox>
            </v-card>
          </v-col>

          <!-- Botones de acción -->
          <v-col cols="12" md="8" lg="8" class="py-2">
            <div class="d-flex justify-center align-center flex-wrap gap-3">
              <v-btn color="success" rounded elevation="2" class="px-6" @click="applyAdvancedFilters">
                <v-icon left size="18">mdi-filter-check</v-icon>
                Aplicar Filtros
              </v-btn>
              <v-btn color="orange" rounded outlined elevation="1" class="px-6" @click="resetAdvancedFilters">
                <v-icon left size="18">mdi-refresh</v-icon>
                Restablecer
              </v-btn>
            </div>
          </v-col>
        </v-row>
      </v-card>
    </v-expand-transition>
  </v-card>
</template>
<script>
import app from "@/store/modules/app";
import moment from "moment";
export default {
  data: () => ({
    filters: {
      location: { catalogue_id: null, id: null },
      business: null,
      warehouse: { id: null, name: null },
    },
    rangeTimeBase: [
      { text: "Hoy", value: "1" },
      { text: "Este mes", value: "2" },
      { text: "Este año", value: "3" },
      { text: "Personalizado", value: "5" },
    ],
    itemsLocations2: [],
    itemsWarehouse: [],
    menuStart: false,
    menuEnd: false,

    // Variables para el selector de almacén jerárquico ARREGLADAS
    warehouseMenuOpen: false,
    warehouseTree: [],
    originalWarehouseTree: [], // Guardar árbol original
    warehouseActive: [],
    expandedNodes: [],
    loadingWarehouses: false,
    loading: false,
    warehouseSelected: [],
    // Variables de búsqueda ARREGLADAS (IGUAL QUE WAREHOUSE.VUE)
    searchTerm: "",
    internalSearchTerm: "", // Para el v-treeview
    searchTimeout: null,
    searchMatchCount: 0,
    isSearchActive: false,
    savedExpandedNodes: [],

    // Variables para filtros avanzados
    showAdvancedFilters: false,
    itemTypes: [],
    taxes: [],
    categorys: [],
    accountCategorys: [],
    units: [],
    advancedFilters: {
      unit: 0,
      item_type: 0,
      item_category: [],
      account_category: 0,
      tax_type: 0,
      inventory_product: 0,
      negative_sale: 0,
      showAll: true,
    },
  }),
  name: "HeaderFilters",
  props: {
    filter_default: {
      type: Array,
      default() {
        return [];
      },
    },
    show_all_option: {
      type: Boolean,
      default: false,
    },
    show_parents: {
      type: Boolean,
      default: true,
    },
    show_date_start: {
      type: Boolean,
      default: false,
    },
  },
  mounted() {
    this.initialize();
    this.initializeAdvancedFilters();
  },
  computed: {
    business_selected: {
      get() {
        return this.$store.state.app.filters.business;
      },
      set(value) {
        this.$store.commit("setBusiness", value);
      },
    },
    location_selected: {
      get() {
        return this.$store.state.app.filters.location;
      },
      set(value) {
        this.$store.commit("setLocation", value);
      },
    },
    warehouse_selected: {
      get() {
        return this.$store.state.app.filters.warehouse;
      },
      set(value) {
        this.$store.commit("setWarehouse", value);
        // Sincronizar también el objeto local filters para compatibilidad
        if (value) {
          this.filters.warehouse = value;
        }
      },
    },
    locationsFrombusiness() {
      let data = Object.assign(
        this.$store.state.auth.locations.filter(
          (element) => element.bus_id == this.business_selected
        ),
        []
      );
      if (this.show_all_option) {
        data.unshift({ text: data.length > 0 ? "Todas" : "Escoja una unidad", value: 0 });
      }
      return data;
    },
    computed_warehouses() {
      // Manejar tanto objetos como IDs para location_selected
      const locationId =
        typeof this.location_selected === "object" && this.location_selected !== null
          ? this.location_selected.value || this.location_selected.id
          : this.location_selected;

      let data = Object.assign(
        this.itemsWarehouse.filter((element) => element.location_id == locationId),
        []
      );

      if (this.show_all_option) {
        data.unshift({ name: "Todos", id: 0 });
      }
      return data;
    },
    // Texto a mostrar en el selector de almacén
    warehouseDisplayText() {
      if (this.filters.warehouse && this.filters.warehouse.id === 0) {
        return "Todos los almacenes";
      } else if (this.filters.warehouse && this.filters.warehouse.name) {
        return this.filters.warehouse.name;
      } else {
        return "Seleccionar almacén";
      }
    },
    dateFormatDMY() {
      return moment(this.dateStart).format("DD/MM/YYYY");
    },
    date2FormatDMY() {
      return moment(this.dateEnd).format("DD/MM/YYYY");
    },
    rangeSelected: {
      get() {
        return this.$store.state.app.rangeSelected;
      },
      set(value) {
        this.$store.commit("setRangeSelected", value);
      },
    },
    dateStart: {
      get() {
        return this.$store.state.app.date;
      },
      set(value) {
        this.$store.commit("setDateStart", value);
      },
    },
    dateEnd: {
      get() {
        return this.$store.state.app.date2;
      },
      set(value) {
        this.$store.commit("setDateEnd", value);
      },
    },

    rangeTime() {
      if (!this.show_date_start) {
        // Si show_date_start es false, solo mostrar "Hoy" y "Personalizado"
        return this.rangeTimeBase.filter(
          (item) => item.value === "1" || item.value === "5"
        );
      }
      // Si show_date_start es true, mostrar todas las opciones
      return this.rangeTimeBase;
    },

    // COMPUTEDS ARREGLADOS (IGUAL QUE WAREHOUSE.VUE)
    currentTreeData() {
      return this.warehouseTree;
    },

    // Custom filter para el v-treeview ARREGLADO
    customFilter() {
      return (item, search, textKey) => {
        if (!search) return true;

        const searchLower = search.toLowerCase();

        // Buscar en nombre
        if (item.name && item.name.toLowerCase().includes(searchLower)) {
          return true;
        }

        // Buscar en dirección
        if (item.address && item.address.toLowerCase().includes(searchLower)) {
          return true;
        }

        // Buscar en ubicación
        if (
          item.location_description_short &&
          item.location_description_short.toLowerCase().includes(searchLower)
        ) {
          return true;
        }

        if (
          item.location_description_long &&
          item.location_description_long.toLowerCase().includes(searchLower)
        ) {
          return true;
        }

        return false;
      };
    },
  },
  watch: {
    "filters.warehouse"() {
      this.refresFilters();
    },

    business_selected() {
      // Al cambiar business, resetear location
      const locations = this.locationsFrombusiness;
      if (locations.length > 0) {
        // Seleccionar la primera ubicación válida para este business
        // Evitamos bucles verificando si ya es la misma
        const firstValidLocation = locations[0];
        if (this.getLocationId(this.location_selected) !== this.getLocationId(firstValidLocation)) {
          this.location_selected = firstValidLocation;
        }
      } else {
        this.location_selected = null;
      }
      this.loadWarehouseTree();
    },

    location_selected() {
      // Al cambiar location, resetear warehouse
      if (this.location_selected) {
        if (this.computed_warehouses.length > 0) {
          const firstWarehouse = this.computed_warehouses[0];
          this.warehouse_selected = firstWarehouse;
        } else {
          this.warehouse_selected = { id: 0, name: "Todos", path: "Todos los almacenes" };
        }
      }
      this.loadWarehouseTree();
    },

    dateStart(val) {
      this.filters.dateStart = val;
      this.$store.commit("setDateStart", val);
      if (this.rangeSelected == "5") {
        this.refresFilters();
      }
    },
    dateEnd(val) {
      this.filters.dateEnd = val;
      this.$store.commit("setDateEnd", val);
      if (this.rangeSelected == "5") {
        this.refresFilters();
      }
    },
    rangeSelected(val) {
      // Guardar en el store
      this.$store.commit("setRangeSelected", val);
      // Solo actualizar el rango si no es personalizado para evitar bucles
      if (val != "5") {
        this.updateRange();
      }
    },

    // WATCHER ARREGLADO - IGUAL QUE WAREHOUSE.VUE
    searchTerm(newVal, oldVal) {
      // Limpiar timeout anterior
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }

      // Si hay texto, activar búsqueda con debounce
      if (newVal && newVal.trim()) {
        this.searchTimeout = setTimeout(() => {
          this.activateSearch();
          this.internalSearchTerm = newVal;
          this.filterWarehouseTree();
        }, 300);
      } else {
        // Si se borra, desactivar búsqueda inmediatamente
        this.deactivateSearch();
        this.internalSearchTerm = "";
        this.searchMatchCount = 0;
        this.resetToOriginalTree();
      }
    },
  },
  methods: {
    async initialize() {
      // 1. Cargar datos básicos de la API
      const warehouses = await this.api_Get("inventory/allWarehouses");
      this.itemsWarehouse = await warehouses.json();

      this.itemsLocations = Object.assign([], this.$store.state.auth.locations);

      // 2. Configurar unidades de negocio si no hay una seleccionada
      if (!this.business_selected && this.$store.state.auth.bussines_units.length > 0) {
        this.business_selected = this.$store.state.auth.bussines_units[0].value || this.$store.state.auth.bussines_units[0].id;
      }

      // 3. Auto-seleccionar location basado en business
      if (this.business_selected && !this.location_selected) {
        const locations = this.locationsFrombusiness.filter(l => l.bus_id == this.business_selected);
        if (locations.length > 0) {
          this.location_selected = locations[0];
        }
      }

      // 4. Configurar warehouse inicial si no hay uno
      if (!this.warehouse_selected && this.computed_warehouses.length > 0) {
        this.warehouse_selected = this.computed_warehouses[0];
      } else if (this.warehouse_selected) {
        // Asegurar que filters.warehouse esté sincronizado con el store al inicio
        this.filters.warehouse = this.warehouse_selected;
      }

      // 5. Configurar fechas
      this.filters.dateStart = this.dateStart;
      this.filters.dateEnd = this.dateEnd;

      // 6. Inicializar el árbol de almacenes
      await this.loadWarehouseTree();

      // 7. Emitir filtros iniciales
      this.refresFilters();
    },

    /**
     * Helper robusto para extraer el ID de una ubicación
     */
    getLocationId(location) {
      if (!location) return null;
      if (typeof location === "object") {
        return location.id ?? location.value ?? null;
      }
      return location;
    },

    // ================================
    // MÉTODOS DE BÚSQUEDA ARREGLADOS (IGUAL QUE WAREHOUSE.VUE)
    // ================================

    /**
     * Maneja el input de búsqueda
     */
    onSearchInput(value) {
      // El watcher se encarga del resto
    },
    handleSelectedUpdate(items) {
      this.selectWarehouse(items);
    },
    /**
     * Activa el modo de búsqueda y expande automáticamente
     */
    activateSearch() {
      if (!this.isSearchActive) {
        // Guardar estado actual
        this.savedExpandedNodes = [...this.expandedNodes];
        this.isSearchActive = true;
      }
    },

    /**
     * Desactiva el modo de búsqueda
     */
    deactivateSearch() {
      if (this.isSearchActive) {
        this.isSearchActive = false;

        // Restaurar estado previo
        this.$nextTick(() => {
          this.expandedNodes = [...this.savedExpandedNodes];
          this.savedExpandedNodes = [];
        });
      }
    },

    /**
     * Filtrar el árbol de almacenes ARREGLADO
     */
    filterWarehouseTree() {
      if (!this.searchTerm.trim()) {
        this.resetToOriginalTree();
        return;
      }

      const search = this.searchTerm.toLowerCase().trim();

      // Usar el árbol original para filtrar
      const sourceTree =
        this.originalWarehouseTree.length > 0
          ? this.originalWarehouseTree
          : this.warehouseTree;

      // Array para recolectar IDs de todos los nodos y sus padres en la ruta
      const matchingNodes = [];
      const matchingPaths = [];

      // Función recursiva de filtrado
      const filterNode = (node, parentPath = []) => {
        const matchesSearch =
          node.name.toLowerCase().includes(search) ||
          (node.address && node.address.toLowerCase().includes(search)) ||
          (node.location_description_short &&
            node.location_description_short.toLowerCase().includes(search)) ||
          (node.location_description_long &&
            node.location_description_long.toLowerCase().includes(search));

        if (matchesSearch) {
          matchingPaths.push(...parentPath);
          matchingPaths.push(node.id);
          matchingNodes.push(node);
        }

        if (node.children && node.children.length) {
          const currentPath = [...parentPath, node.id];
          const filteredChildren = node.children
            .map((child) => filterNode(child, currentPath))
            .filter((child) => child !== null);

          if (matchesSearch || filteredChildren.length > 0) {
            if (filteredChildren.length > 0) {
              matchingPaths.push(node.id);
            }

            return {
              ...node,
              children: filteredChildren,
            };
          }
        } else if (matchesSearch) {
          return { ...node };
        }

        return null;
      };

      // Aplicar filtro
      const filtered = sourceTree
        .map((node) => filterNode(node, []))
        .filter((node) => node !== null);

      // Actualizar el árbol mostrado
      this.warehouseTree = filtered;

      // Expandir nodos automáticamente
      if (filtered.length > 0 && matchingPaths.length > 0) {
        const uniqueIds = [...new Set(matchingPaths)];
        this.expandedNodes = [...uniqueIds];
        this.searchMatchCount = matchingNodes.length;

        this.$nextTick(() => {
          const treeView = this.$refs.warehouseTree;
          if (treeView) {
            treeView.$forceUpdate();
          }
        });
      } else {
        this.searchMatchCount = 0;
      }
    },

    /**
     * Resetear al árbol original
     */
    resetToOriginalTree() {
      if (this.originalWarehouseTree.length > 0) {
        this.warehouseTree = JSON.parse(JSON.stringify(this.originalWarehouseTree));
      }
      this.expandedNodes = [];
      this.isSearchActive = false;
      this.searchMatchCount = 0;
    },

    /**
     * Limpia la búsqueda
     */
    clearSearch() {
      this.searchTerm = "";
      this.deactivateSearch();
      this.resetToOriginalTree();
    },

    /**
     * Resalta el texto de búsqueda
     */
    highlightSearchText(text) {
      if (!text || !this.searchTerm) {
        return text || "";
      }

      const searchTerm = this.searchTerm.toLowerCase();
      const textLower = text.toLowerCase();

      if (!textLower.includes(searchTerm)) {
        return text;
      }

      const regex = new RegExp(`(${this.escapeRegex(this.searchTerm)})`, "gi");
      return text.replace(regex, '<mark class="search-highlight">$1</mark>');
    },

    /**
     * Escapa caracteres especiales para regex
     */
    escapeRegex(string) {
      return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    },

    /**
     * Verifica si un nodo está resaltado
     */
    isNodeHighlighted(node) {
      if (!this.searchTerm || !node) return false;
      return this.customFilter(node, this.searchTerm);
    },

    /**
     * Expandir todos los nodos
     */
    expandAllNodes() {
      this.loading = true;

      try {
        const getAllParentNodeIds = (nodes) => {
          let ids = [];
          nodes.forEach((node) => {
            if (node.children && node.children.length > 0) {
              ids.push(node.id);
              ids = ids.concat(getAllParentNodeIds(node.children));
            }
          });
          return ids;
        };

        const parentIds = getAllParentNodeIds(this.currentTreeData);

        setTimeout(() => {
          this.expandedNodes = parentIds;

          const treeComponent = this.$refs.warehouseTree;
          if (treeComponent) {
            treeComponent.$forceUpdate();
            if (treeComponent.updateAll) {
              treeComponent.updateAll(true);
            }
          }

          this.loading = false;
        }, 200);
      } catch (error) {
        console.error("Error al expandir nodos:", error);
        this.loading = false;
      }
    },

    /**
     * Colapsar todos los nodos
     */
    collapseAllNodes() {
      this.loading = true;

      try {
        if (this.isSearchActive) {
          this.clearSearch();
        }

        this.expandedNodes = [];

        const treeView = this.$refs.warehouseTree;
        if (treeView) {
          setTimeout(() => {
            treeView.$forceUpdate();
            this.loading = false;
          }, 100);
        } else {
          this.loading = false;
        }
      } catch (error) {
        console.error("Error al colapsar el árbol:", error);
        this.loading = false;
      }
    },

    // ================================
    // MÉTODOS ORIGINALES ARREGLADOS
    // ================================

    // Método para cargar el árbol de almacenes ARREGLADO
    async loadWarehouseTree() {
      this.loadingWarehouses = true;
      try {
        let filters = "";
        if (this.business_selected) {
          filters = ` WHERE w.BUSINESS_UNITS_ID = ${this.business_selected}`;
          const locationId = this.getLocationId(this.location_selected);

          if (locationId && locationId !== 0) {
            filters += ` AND w.LOCATION_ID = ${locationId}`;
          }
        }

        const data = { filters };
        const response = await this.api_Post("inventory/warehouse/tree", data);

        if (response.status === 200) {
          const result = await response.json();
          this.warehouseTree = this.buildTree(result);
          // ARREGLO: Guardar árbol original para búsquedas
          this.originalWarehouseTree = JSON.parse(JSON.stringify(this.warehouseTree));

          // Limpiar búsqueda al cargar nuevos datos
          this.clearSearch();

          // Si ya hay un almacén seleccionado, establecer el nodo activo
          if (this.filters.warehouse && this.filters.warehouse.id) {
            this.setActiveWarehouseInTree(this.filters.warehouse.id);
          }
        } else {
          console.error("Error al cargar almacenes");
          this.warehouseTree = [];
          this.originalWarehouseTree = [];
        }
      } catch (error) {
        console.error("Error al cargar el árbol de almacenes:", error);
        this.warehouseTree = [];
        this.originalWarehouseTree = [];
      } finally {
        this.loadingWarehouses = false;
      }
    },

    // Build tree structure
    buildTree(items) {
      const itemMap = {};
      const roots = [];

      // Create item map
      items.forEach((item) => {
        itemMap[item.id] = { ...item, children: [] };
      });

      // Build tree
      items.forEach((item) => {
        if (item.parent_warehouse_id === null) {
          roots.push(itemMap[item.id]);
        } else if (itemMap[item.parent_warehouse_id]) {
          // Only add children if show_parents is true
          if (this.show_parents) {
            itemMap[item.parent_warehouse_id].children.push(itemMap[item.id]);
          }
        }
      });

      return roots;
    },

    // Manejar la actualización de nodos expandidos ARREGLADO
    handleOpenUpdate(nodes) {
      if (!this.isSearchActive) {
        this.expandedNodes = nodes;
      }
    },

    // Manejar la actualización de nodos activos/seleccionados
    handleActiveUpdate(items) {
      this.selectWarehouse(items);
    },

    // Establecer el nodo activo basado en el ID del almacén
    setActiveWarehouseInTree(warehouseId) {
      this.warehouseActive = [];

      const findNodeById = (nodes) => {
        for (const node of nodes) {
          if (node.id === warehouseId) {
            this.warehouseActive = [node];
            return true;
          }

          if (node.children && node.children.length > 0) {
            if (findNodeById(node.children)) {
              return true;
            }
          }
        }

        return false;
      };

      findNodeById(this.warehouseTree);
    },

    // Manejar la selección de un almacén
    selectWarehouse(nodes) {
      if (nodes.length > 0) {
        const selectedNode = nodes[0];

        this.warehouseSelected = [selectedNode];
        this.warehouseActive = [selectedNode];

        this.filters.warehouse = {
          id: selectedNode.id,
          name: selectedNode.name,
          isParent: selectedNode.children && selectedNode.children.length > 0,
          path: this.getWarehousePath(selectedNode),
        };

        this.warehouseMenuOpen = false;
        this.refresFilters();
      } else {
        this.warehouseSelected = [];
        this.warehouseActive = [];
      }
    },

    // Obtener la ruta completa del almacén seleccionado
    getWarehousePath(node) {
      return node.name;
    },

    // Seleccionar la opción "Todos los almacenes"
    selectAllWarehouses() {
      this.filters.warehouse = { id: 0, name: "Todos", path: "Todos los almacenes" };
      this.warehouseActive = [];
      this.warehouseMenuOpen = false;
      this.refresFilters();
    },

    refresFilters() {
      this.validate_all_item_category();
      this.filters.business = this.business_selected;
      this.filters.location = this.location_selected;
      const business = this.$store.state.auth.bussines_units.find(
        (element) => element.id == this.business_selected
      );
      this.filters.business_unit = {
        id: business.id,
        name: business.description_long,
      };

      // Combinar filtros básicos con filtros avanzados
      const combinedFilters = {
        ...this.filters,
        ...this.advancedFilters,
      };



      this.$emit("refreshWarehouse", combinedFilters);
    },
    updateRange() {
      if (this.rangeSelected == "5") {
        return;
      }

      let dateEnd;
      let dateStart = null;

      if (this.rangeSelected == "1") {
        // Hoy
        dateEnd = moment().format("YYYY-MM-DD");
      } else if (this.rangeSelected == "2") {
        // Este mes
        dateStart = moment().startOf("month").format("YYYY-MM-DD");
        dateEnd = moment().endOf("month").format("YYYY-MM-DD");
      } else if (this.rangeSelected == "3") {
        // Este año
        dateStart = moment().startOf("year").format("YYYY-MM-DD");
        dateEnd = moment().endOf("year").format("YYYY-MM-DD");
      }

      // Actualizar fechas
      if (dateEnd) {
        this.dateEnd = dateEnd;
        this.filters.dateEnd = dateEnd;

        // Solo establecer fecha desde si show_date_start es true y hay una fecha desde
        if (this.show_date_start && dateStart) {
          this.dateStart = dateStart;
          this.filters.dateStart = dateStart;
        } else {
          // Limpiar fecha desde si no se debe mostrar
          this.dateStart = null;
          this.filters.dateStart = null;
        }

        // Actualizar el store
        this.$store.commit("setDateRange", {
          dateStart: this.show_date_start ? dateStart : null,
          dateEnd: dateEnd,
        });

        this.refresFilters();
      }
    },

    // Métodos para filtros avanzados
    async initializeAdvancedFilters() {
      try {
        //----------------UNIDADES--------------------
        const resultUnits = await this.api_Get("inventory/units");
        this.units = await resultUnits.json();
        this.units.unshift({ id: 0, name: "TODO" });

        //----------------ITEM TYPES--------------------
        const resultItemTypes = await this.api_Get("inventory/itemTypes");
        this.itemTypes = await resultItemTypes.json();
        this.itemTypes.unshift({ id: 0, name: "TODO" });

        //----------------TAXES-------------------------
        const taxesRequest = await this.api_Get("accounting/taxes");
        this.taxes = await taxesRequest.json();
        this.taxes.unshift({ id: 0, name: "TODO" });

        //----------------CATEGORYS-------------------------
        const categoryRequest = await this.api_Get("inventory/categories");
        const allCategories = await categoryRequest.json();
        // Solo mostrar categorías raíz (sin categoría padre)
        this.categorys = allCategories.filter(
          (category) => category.parent_category_id == null
        );
        this.categorys.unshift({ id: 0, name: "TODO" });

        //----------------ACCOUNT CATEGORYS-------------------------
        const resultAccounts = await this.api_Get("accounting/account_categories");
        this.accountCategorys = await resultAccounts.json();
        this.accountCategorys.unshift({ id: 0, name: "TODO" });

        // Inicializar valores por defecto
        this.advancedFilters.inventory_product = 0;
        this.advancedFilters.negative_sale = 0;
      } catch (error) {
        console.error("Error al cargar filtros avanzados:", error);
      }
    },

    toggleAdvancedFilters() {
      this.showAdvancedFilters = !this.showAdvancedFilters;
    },

    applyAdvancedFilters() {
      // Usar refresFilters para mantener consistencia
      this.refresFilters();
    },

    resetAdvancedFilters() {
      this.advancedFilters = {
        unit: 0,
        item_type: 0,
        item_category: [],
        account_category: 0,
        tax_type: 0,
        inventory_product: 0,
        negative_sale: 0,
        showAll: true,
      };

      // Aplicar filtros después del reset
      this.refresFilters();
    },
    validate_all_item_category() {
      if (this.advancedFilters.item_category[this.advancedFilters.item_category.length - 1] == 0 && this.advancedFilters.item_category.length > 1
      ) {
        this.advancedFilters.item_category = [0];
      }
      if (this.advancedFilters.item_category[0] == 0 && this.advancedFilters.item_category.length > 1
      ) {
        this.advancedFilters.item_category = this.advancedFilters.item_category.filter((item) => item != 0);
      }
    },
  },
  components: {},
};
</script>

<style scoped>
/* ================================
   ESTILOS PARA FILTROS AVANZADOS
   ================================ */

.advanced-filters-section {
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border: 1px solid #e9ecef;
  border-radius: 12px;
  padding: 20px;
  margin-top: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.advanced-filters-title {
  color: var(--v-primary-base);
  font-weight: 600;
  font-size: 1.1rem;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.filter-row {
  margin-bottom: 16px;
}

.filter-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #e9ecef;
}

.expand-button {
  transition: all 0.3s ease;
}

.expand-button:hover {
  transform: scale(1.05);
}

.advanced-filter-field {
  transition: all 0.3s ease;
}

.advanced-filter-field:hover {
  transform: translateY(-1px);
}

/* ================================
   ESTILOS DEL SELECTOR DE ALMACÉN MEJORADO
   ================================ */

.warehouse-select-card {
  border-radius: 12px !important;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12) !important;
}

.warehouse-select-title {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-bottom: 1px solid #dee2e6;
  padding: 16px 20px;
}

.warehouse-title {
  color: var(--v-secondary-base);
  font-weight: 500;
  font-size: 1.1rem;
  margin: 0;
}

.warehouse-subtitle {
  color: #6c757d;
  font-size: 0.85rem;
  margin-top: 2px;
}

.warehouse-controls {
  display: flex;
  align-items: center;
  gap: 8px;
}

.search-field {
  max-width: 250px;
  min-width: 200px;
  transition: all 0.3s ease;
}

.search-field.v-input--is-focused {
  transform: scale(1.02);
}

.search-results {
  color: var(--v-success-base);
  font-weight: 500;
  animation: fadeIn 0.3s ease;
}

.search-highlight {
  background: linear-gradient(135deg, #fff59d 0%, #ffeb3b 100%);
  padding: 2px 4px;
  border-radius: 3px;
  font-weight: 600;
  color: #333;
  box-shadow: 0 1px 3px rgba(255, 235, 59, 0.4);
}

/* Animación para resultados de búsqueda */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ================================
   ESTILOS DEL TREEVIEW
   ================================ */

.warehouse-tree {
  max-height: 350px;
  overflow-y: auto;
}

.warehouse-tree::-webkit-scrollbar {
  width: 6px;
}

.warehouse-tree::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.warehouse-tree::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
  transition: background 0.3s ease;
}

.warehouse-tree::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

.warehouse-tree .v-treeview-node__root {
  padding: 6px 8px;
  margin-bottom: 2px;
  border-radius: 8px;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  border-left: 3px solid transparent;
}

.warehouse-tree .v-treeview-node__root:hover {
  background: linear-gradient(135deg,
      rgba(25, 118, 210, 0.08) 0%,
      rgba(25, 118, 210, 0.12) 100%);
  border-left-color: var(--v-primary-base);
  transform: translateX(2px);
  box-shadow: 0 2px 8px rgba(25, 118, 210, 0.15);
}

.warehouse-tree .v-treeview-node--active>.v-treeview-node__root {
  background: linear-gradient(135deg,
      rgba(25, 118, 210, 0.15) 0%,
      rgba(25, 118, 210, 0.2) 100%);
  border-left-color: var(--v-primary-base);
  border-left-width: 4px;
}

.warehouse-node {
  width: 100%;
}

.warehouse-name {
  font-weight: 500;
  line-height: 1.4;
  transition: color 0.3s ease;
}

.warehouse-details {
  font-size: 0.75rem;
  margin-top: 2px;
}

.location-info {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 180px;
}

/* Empty state */
.empty-warehouse-state {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  padding: 20px;
}

.empty-warehouse-content {
  text-align: center;
  max-width: 300px;
}

.empty-warehouse-title {
  color: var(--v-secondary-base);
  font-weight: 400;
  margin-bottom: 8px;
}

.empty-warehouse-subtitle {
  font-size: 14px;
  line-height: 1.5;
}

/* Pulse animation for search highlights */
@keyframes pulse {
  0% {
    transform: scale(1);
    opacity: 1;
  }

  50% {
    transform: scale(1.1);
    opacity: 0.8;
  }

  100% {
    transform: scale(1);
    opacity: 1;
  }
}

.pulse-icon {
  animation: pulse 1.5s infinite;
  color: var(--v-primary-base) !important;
}

/* Chips */
.v-chip.v-chip--x-small {
  font-size: 0.65rem;
  height: 18px;
  padding: 0 6px;
  font-weight: 500;
}

/* Responsive */
@media (max-width: 600px) {
  .warehouse-controls {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }

  .search-field {
    max-width: none;
    min-width: auto;
  }

  .warehouse-select-title {
    padding: 12px 16px;
  }

  .warehouse-tree {
    max-height: 250px;
  }
}

/* Hover effects */
.v-btn:not(.v-btn--disabled):hover {
  transform: translateY(-1px);
  transition: transform 0.2s ease;
}

/* Focus states */
.v-text-field--outlined.v-input--is-focused fieldset {
  border-color: var(--v-primary-base) !important;
  border-width: 2px !important;
}

/* Estilos adicionales para HeaderFilters */
.v-card {
  transition: all 0.3s ease;
}

.v-card:hover {
  transform: translateY(-2px);
}

/* Mejoras visuales para filtros avanzados */
.gap-3 {
  gap: 12px;
}

/* Animaciones suaves para los filtros */
.v-expand-transition-enter-active,
.v-expand-transition-leave-active {
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
}

/* Estilos para el checkbox personalizado */
.v-input--checkbox .v-input__slot {
  margin-bottom: 0;
}

/* Mejora visual para campos de filtro */
.v-text-field--outlined>.v-input__control>.v-input__slot {
  border-radius: 8px;
}

/* Sombras suaves para botones */
.v-btn {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

.v-btn:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

/* Estilo para el divider del título */
.v-divider {
  border-color: rgba(var(--v-primary-base), 0.3);
  border-width: 1px;
}

/* Sombra adicional para cards */
.v-card--hover {
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15) !important;
}

.v-autocomplete .v-input__control .v-input__slot {
  transition: all 0.3s ease;
}

.v-autocomplete:hover .v-input__control .v-input__slot {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.v-text-field .v-input__control .v-input__slot {
  transition: all 0.3s ease;
}

.v-text-field:hover .v-input__control .v-input__slot {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.v-icon {
  transition: all 0.3s ease;
}

.v-input--is-focused .v-icon {
  transform: scale(1.1);
}

.v-date-picker {
  border-radius: 12px !important;
  overflow: hidden;
}

.v-date-picker__header {
  border-radius: 12px 12px 0 0 !important;
}
</style>
